import React, { useEffect, useMemo, useRef, useState } from 'react';
import { audioTemplate } from '../../data/part1';

// Expect items as an object like { key: { text: 'wyuv', ... }, ... }
// We'll present each item's letters as options and ask user to pick a target letter

const AlphabetScreen = ({ items = {}, onComplete }) => {
  const entries = useMemo(() => Object.values(items || {}), [items]);
  const [index, setIndex] = useState(0);
  const [selectedLetter, setSelectedLetter] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null); // 'correct' | 'wrong' | null
  // Removed attempt state - no longer needed with single chance
  const [correctCount, setCorrectCount] = useState(0);
  const [selectionEnabled, setSelectionEnabled] = useState(false);
  const audioRef = useRef(null);
  const instructionPlayedRef = useRef(false);
  const firstQuestionAudioPlayedRef = useRef(false);
  const [timer, setTimer] = useState(10);
  const timerRef = useRef(null);
  // Detect iOS browser
  const isIOS = useMemo(() => {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
    return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  }, []);
  const [showStart, setShowStart] = useState(() => isIOS);

  const current = entries[index] || {};
  const letters = current.choices || (current.text || '').split('');
  const target = current.answerKey || letters[0] || null; // prefer provided answerKey
  const audioUrl = useMemo(() => {
    return target ? audioTemplate.replace('{{key}}', String(target)) : null;
  }, [target]);


  // Play instruction and question audio
  useEffect(() => {
    if (isIOS && showStart) return;
    let cleanupFns = [];
    // cleanup any prior audio
    if (audioRef.current) {
      try { audioRef.current.pause(); } catch {}
      audioRef.current = null;
    }
    setSelectionEnabled(false);
    setSelectedLetter(null);
    setSelectedStatus(null);
    setTimer(10);

    // Helper function to play question audio
    const playQuestionAudio = () => {
      if (!audioUrl) {
        setSelectionEnabled(true);
        return;
      }
      
      // For iOS, try to reuse existing audio element if possible to maintain audio context
      if (isIOS && audioRef.current && audioRef.current.src !== audioUrl) {
        try {
          audioRef.current.src = audioUrl;
          audioRef.current.load();
        } catch (e) {
          // If reusing fails, create new element
          audioRef.current = new Audio(audioUrl);
        }
      } else if (!audioRef.current) {
        audioRef.current = new Audio(audioUrl);
      }
      
      const el = audioRef.current;
      const onEnded = () => {
        setSelectionEnabled(true);
      };
      const onError = (error) => {
        console.warn('Question audio error:', error);
        setSelectionEnabled(true);
      };
      
      // Remove any existing listeners
      el.removeEventListener('ended', onEnded);
      el.removeEventListener('error', onError);
      
      // Add new listeners
      el.addEventListener('ended', onEnded);
      el.addEventListener('error', onError);
      
      // For iOS, add additional events to handle playback issues
      if (isIOS) {
        const onCanPlay = () => {
          el.play().catch((error) => {
            console.warn('iOS audio play failed:', error);
            setSelectionEnabled(true);
          });
        };
        
        if (el.readyState >= 2) { // HAVE_CURRENT_DATA
          onCanPlay();
        } else {
          el.addEventListener('canplay', onCanPlay, { once: true });
        }
      } else {
        el.play().catch((error) => {
          console.warn('Audio play failed:', error);
          setSelectionEnabled(true);
        });
      }
      
      cleanupFns.push(() => {
        el.removeEventListener('ended', onEnded);
        el.removeEventListener('error', onError);
        try { el.pause(); } catch {}
      });
    };

    // For the first question, play instruction first, then question audio
    if (!instructionPlayedRef.current && index === 0) {
      const instructionAudio = new Audio(process.env.PUBLIC_URL ? `${process.env.PUBLIC_URL}/audio/sletter.mp3` : '/audio/sletter.mp3');
      const onInstructionEnd = () => {
        instructionPlayedRef.current = true;
        // Play question audio immediately after instruction (no delay on iOS to maintain audio context)
        if (isIOS) {
          playQuestionAudio();
        } else {
          // Add delay for non-iOS devices
          setTimeout(() => {
            playQuestionAudio();
          }, 6000); // 6 second delay
        }
      };
      
      const onInstructionError = () => {
        instructionPlayedRef.current = true;
        onInstructionEnd();
      };
      
      instructionAudio.addEventListener('ended', onInstructionEnd);
      instructionAudio.addEventListener('error', onInstructionError);
      
      // Try to play the instruction audio
      instructionAudio.play().catch((error) => {
        onInstructionError();
      });
      
      cleanupFns.push(() => {
        instructionAudio.removeEventListener('ended', onInstructionEnd);
        instructionAudio.removeEventListener('error', onInstructionError);
        try { instructionAudio.pause(); } catch {}
      });
      return () => { cleanupFns.forEach(fn => fn()); };
    }

    // For all other questions, play question audio immediately
    playQuestionAudio();
    return () => { cleanupFns.forEach(fn => fn()); };
  // re-run when the question index changes
  }, [index, audioUrl, showStart]);

  const replayAudio = () => {
    if (!audioUrl) return;
    setSelectionEnabled(false);
    try {
      if (audioRef.current) {
        // For iOS, ensure we can replay the current audio
        if (isIOS) {
          audioRef.current.currentTime = 0;
          const playPromise = audioRef.current.play();
          if (playPromise) {
            playPromise.catch(() => {
              // If replay fails on iOS, try creating new audio element
              const newAudio = new Audio(audioUrl);
              audioRef.current = newAudio;
              const onEnded = () => setSelectionEnabled(true);
              const onError = () => setSelectionEnabled(true);
              newAudio.addEventListener('ended', onEnded);
              newAudio.addEventListener('error', onError);
              newAudio.play().catch(() => setSelectionEnabled(true));
            });
          }
        } else {
          audioRef.current.currentTime = 0;
          audioRef.current.play().catch(() => setSelectionEnabled(true));
        }
      } else {
        const el = new Audio(audioUrl);
        audioRef.current = el;
        const onEnded = () => setSelectionEnabled(true);
        const onError = () => setSelectionEnabled(true);
        el.addEventListener('ended', onEnded);
        el.addEventListener('error', onError);
        el.play().catch(() => setSelectionEnabled(true));
      }
    } catch {
      setSelectionEnabled(true);
    }
  };

  const handleLetterClick = (letter) => {
    if (!selectionEnabled) return;
    setSelectedLetter(letter);
    const isCorrect = letter === target;
    setSelectedStatus(isCorrect ? 'correct' : 'wrong');
    setSelectionEnabled(false);
    // stop timer when answered
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }

    const DELAY = 1200; // ms between answers for feedback

    if (isCorrect) {
      setCorrectCount(c => c + 1);
      setTimeout(() => {
        const next = index + 1;
        setSelectedLetter(null);
        setSelectedStatus(null);
        if (next < entries.length) {
          setIndex(next);
        } else {
          const passedAll = (correctCount + 1) === entries.length;
          onComplete && onComplete({ passedAll, correctCount: correctCount + 1, totalQuestions: entries.length });
        }
      }, DELAY);
      return;
    }

    // Wrong answer - move to next question immediately
    setTimeout(() => {
      const next = index + 1;
      setSelectedLetter(null);
      setSelectedStatus(null);
      if (next < entries.length) {
        setIndex(next);
      } else {
        const passedAll = correctCount === entries.length; // no increment on wrong
        onComplete && onComplete({ passedAll, correctCount, totalQuestions: entries.length });
      }
    }, DELAY);
  };

  // Timer effect: counts down from 10 when selection is enabled; on timeout mark as missed and advance
  useEffect(() => {
    if (!selectionEnabled) {
      // Clear timer when selection is disabled
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      return;
    }
    
    // Start timer when selection is enabled
    if (timerRef.current) clearInterval(timerRef.current);
    setTimer(10);
    timerRef.current = setInterval(() => {
      setTimer((t) => {
        if (t <= 1) {
          clearInterval(timerRef.current);
          timerRef.current = null;
          // If no selection was made, treat as missed
          setSelectedLetter(null);
          setSelectedStatus('wrong');
          setSelectionEnabled(false);
          
          // Play ding audio on timeout
          if (isIOS) {
            // On iOS, skip ding audio to maintain audio context for next question
            const DELAY = 200; // Shorter delay on iOS
            setTimeout(() => {
              const next = index + 1;
              setSelectedLetter(null);
              setSelectedStatus(null);
              if (next < entries.length) {
                setIndex(next);
              } else {
                const passedAll = (correctCount) === entries.length;
                onComplete && onComplete({ passedAll, correctCount, totalQuestions: entries.length });
              }
            }, DELAY);
          } else {
            // Play ding audio on non-iOS devices
            try {
              const dingAudio = new Audio(process.env.PUBLIC_URL ? `${process.env.PUBLIC_URL}/audio/ding.mp3` : '/audio/ding.mp3');
              dingAudio.play().catch(() => {});
            } catch {}
            
            const DELAY = 1200;
            setTimeout(() => {
              const next = index + 1;
              setSelectedLetter(null);
              setSelectedStatus(null);
              if (next < entries.length) {
                setIndex(next);
              } else {
                const passedAll = (correctCount) === entries.length;
                onComplete && onComplete({ passedAll, correctCount, totalQuestions: entries.length });
              }
            }, DELAY);
          }
          return 0;
        }
        return t - 1;
      });
    }, 1000);
    
    return () => { 
      if (timerRef.current) { 
        clearInterval(timerRef.current); 
        timerRef.current = null; 
      } 
    };
  }, [selectionEnabled, index, entries.length, correctCount, onComplete]);

  return (
    <div className="content-area" style={{ position: 'relative' }}>
      {showStart && (
        <div style={{
          position: 'absolute',
          zIndex: 10,
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: 'rgba(255,255,255,0.95)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <button
            style={{
              fontSize: 28,
              padding: '18px 44px',
              borderRadius: 16,
              border: '3px solid var(--primary-color)',
              background: 'var(--primary-color)',
              color: 'var(--white)',
              fontFamily: 'var(--font-albert)',
              fontWeight: 700,
              cursor: 'pointer',
              boxShadow: '0 2px 12px rgba(0,0,0,0.08)'
            }}
            onClick={() => setShowStart(false)}
          >
            Tap to Start
          </button>
        </div>
      )}
      {/* Timer */}
      <div className="timer">
        <span className="timer-number">{timer}</span>
        {/* Clock icon */}
        <svg width="29" height="29" viewBox="0 0 29 29" fill="none">
          <circle cx="14.5" cy="14.5" r="10.4213" stroke="var(--primary-color)" strokeWidth="3"/>
          <path d="M2 5L7.01953 0.607959" stroke="var(--primary-color)" strokeWidth="3" strokeLinecap="round"/>
          <path d="M14.8623 16V9.72583" stroke="var(--primary-color)" strokeWidth="3" strokeLinecap="round"/>
          <path d="M10.1564 14.7456L14.8621 16.0004" stroke="var(--primary-color)" strokeWidth="3" strokeLinecap="round"/>
          <path d="M5.94385 26.1906L7.46667 24.007" stroke="var(--primary-color)" strokeWidth="3" strokeLinecap="round"/>
          <path d="M27.544 5L22.525 0.607959" stroke="var(--primary-color)" strokeWidth="3" strokeLinecap="round"/>
          <path d="M23.6006 26.1906L22.0778 24.007" stroke="var(--primary-color)" strokeWidth="3" strokeLinecap="round"/>
        </svg>
      </div>

      {/* Main content */}
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center',
        flex: 1,
        gap: '60px',
        padding: '20px'
      }}>
        {/* Letter grid */}
        <div className="letter-grid" style={{ opacity: selectionEnabled ? 1 : 0.6 }}>
          {letters.map((letter, idx) => {
            const isSelected = selectedLetter === letter;
            let borderColor = 'var(--primary-color)';
            let backgroundColor = isSelected ? 'var(--primary-color)' : 'var(--background-color)';
            let color = isSelected ? 'var(--white)' : 'var(--primary-color)';
            if (isSelected && selectedStatus === 'correct') {
              borderColor = 'var(--success-color, #22c55e)';
              backgroundColor = 'var(--success-color, #22c55e)';
              color = 'var(--white)';
            } else if (isSelected && selectedStatus === 'wrong') {
              borderColor = 'var(--danger-color, #ef4444)';
              backgroundColor = 'var(--danger-color, #ef4444)';
              color = 'var(--white)';
            }
            return (
              <div
                key={idx}
                className="letter-box"
                onClick={() => handleLetterClick(letter)}
                style={{
                  width: '167px',
                  height: '167px',
                  cursor: selectionEnabled ? 'pointer' : 'not-allowed',
                  backgroundColor,
                  color,
                  transition: 'all 0.2s ease',
                  border: `5px solid ${borderColor}`,
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '72px',
                  fontWeight: 700,
                  fontFamily: "'KG Primary Penmanship', var(--font-albert)",
                  userSelect: 'none'
                }}
              >
                {letter}
              </div>
            );
          })}
        </div>

        {/* Listen Again button */}
        <button
          onClick={replayAudio}
          disabled={!audioUrl}
          style={{ 
            display: 'flex',
            alignItems: 'center', 
            gap: '10px',
            cursor: audioUrl ? 'pointer' : 'not-allowed',
            padding: '10px 20px',
            borderRadius: '10px',
            transition: 'all 0.2s ease',
            border: '1px solid var(--primary-color)',
            background: 'transparent'
          }}
        >
          {/* Volume icon */}
          <svg width="28" height="28" viewBox="0 0 29 29" fill="none">
            <path 
              d="M22.7485 6.25146C24.9357 8.43929 26.1643 11.4062 26.1643 14.4998C26.1643 17.5934 24.9357 20.5603 22.7485 22.7481M18.6302 10.3698C19.7237 11.4637 20.3381 12.9472 20.3381 14.494C20.3381 16.0408 19.7237 17.5242 18.6302 18.6181M13.3335 6.33313L7.50016 10.9998H2.8335V17.9998H7.50016L13.3335 22.6665V6.33313Z" 
              stroke="#1E1E1E" 
              strokeWidth="2.33333" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            />
          </svg>
          
          <span style={{ 
            color: 'var(--black)', 
            fontFamily: 'var(--font-albert)', 
            fontSize: '19px',
            fontWeight: '500',
            lineHeight: '24px'
          }}>
            Listen and select the correct letter
          </span>
        </button>
      </div>
    </div>
  );
};

export default AlphabetScreen;
